import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { BsModalService } from 'ngx-bootstrap/modal';
import { ActivatedRoute, Router } from '@angular/router';

import { SharedService } from 'src/app/services/shared.service';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { ApiService } from 'src/app/services/api.service';

@Component({
    selector: 'app-scan-upload',
    imports: [FormsModule],
    templateUrl: './scan-upload.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './scan-upload.component.scss'
})
export class ScanUploadComponent implements OnInit {
  @Input() claimData: any;
  @Output() goback = new EventEmitter<string>();
  @ViewChild('cameraInput') cameraInput!: ElementRef<HTMLInputElement>;
  @ViewChild('galleryInput') galleryInput!: ElementRef<HTMLInputElement>;

  manualUpload = true;
  progressWidth = '0%';
  agreedToTerms = false;
  acknowledgedFraudNotice = false;

  tempClaimId: string = '';
  requestIds: any[] = [];
  currentStep = 1;
  contractId: string = '';
  doctorId: string = '';
  serviceBreakdown: any;

  claimDocs: any[] = [];
  uploadedFiles: { file: File, document_type: number, more_id?: number }[] = [];

  documentType: any;
  showUploadFromModal = false;
  selectedDoc: any = null;
  claimId: any;
  showAddmore: boolean = true

  constructor(
    private modalService: BsModalService,
    private shared: SharedService,
    private loader: LoaderService,
    private api: ApiService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['tempId']) {
        this.tempClaimId = params['tempId'];
        this.fetchClaimDocuments();
      } else {
        this.showAddmore = false
        this.claimId = params['claimId'];
        this.fetchMoreDocuments();

      }
    });
  }

  fetchClaimDocuments(): void {
    const payload = { temp_claim_id: this.tempClaimId };

    this.api.api(payload, 'auth/get_claim_doc_list', 'get').subscribe((res: any) => {
      if (res?.is_error === 0) {
        this.claimDocs = res.claim_doc_list.map((doc: any) => ({
          name: doc.name,
          required: doc.is_required,
          uploaded: false,
          doc_order: doc.doc_order,
          document_type: doc.document_type,
          claim_doc_id: doc.claim_doc_id
        }));
        this.doctorId = res.doctor_id;
      }
    });
  }
  fetchMoreDocuments(): void {
    const payload = { claim_id: this.claimId };
    this.api.api(payload, 'auth/get_claim_more_doc_list', 'get').subscribe((res: any) => {
      if (res?.is_error === 0) {
        this.claimDocs = res.claim_doc_list.map((doc: any) => ({
          name: doc.name,
          required: true,
          uploaded: false,
          doc_order: doc.doc_order,
          document_type: doc.document_type,
          more_id: doc.more_id,
          claim_doc_id: doc.claim_doc_id
        }));
        this.doctorId = res.doctor_id;
        this.contractId =  res.contract_id;
      }
    });
  }

  toggleUploadMode(): void {
    this.manualUpload = !this.manualUpload;
  }

  addDocument(event: Event): void {
    event.preventDefault();
    this.claimDocs.push({
      name: 'Additional Document',
      required: false,
      uploaded: false,
      document_type: 4
    });
  }

  viewDocument(doc: any): void {
    this.shared.customPopups(`Previewing document: ${doc.name}`, 2);
  }

  removeDocument(doc: any): void {
    if (doc.claim_doc_id) {
      this.loader.showLoader();
      const payload = {
        claim_doc_id: doc.claim_doc_id,
        temp_claim_id: this.tempClaimId,
        request_id: this.requestIds
      };
      this.api.api(payload, 'claims/remove_claim_doc', 'POST').subscribe(
        () => {
          doc.uploaded = false;
          this.shared.customPopups('Document Removed', 3);
          this.loader.hideLoader();
        },
        () => {
          this.shared.customPopups('Error removing document', 1);
          this.loader.hideLoader();
        }
      );
    } else {
      const index = this.claimDocs.indexOf(doc);
      if (index > -1) {
        doc.uploaded = false;
        // this.claimDocs.splice(index, 1);
        this.shared.customPopups('Document Removed', 3);
      }
    }
  }

  openUploadFromModal(doc: any): void {
    this.selectedDoc = doc;
    this.showUploadFromModal = true;
  }

  closeUploadFromModal(): void {
    this.showUploadFromModal = false;
    this.selectedDoc = null;
  }

  handleFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (file) {
      this.checkClaimStatus(file);
    }
  }

  checkClaimStatus(file: File): void {
    let payload: any = {
      temp_claim_id: this.tempClaimId,
      document_type: this.selectedDoc?.document_type,
      doctor_id: this.doctorId
    };

    let apiUrl = 'auth/check_claim_doc_status';

    if (this.claimId) {
      payload.more_id = this.selectedDoc.more_id; // <-- use a defined property instead of bare `more_id`
      apiUrl = 'auth/check_more_doc_status';
    }


    this.loader.showLoader();
    this.api.uploadApi(payload, apiUrl).subscribe(
      (res: any) => {
        if (res?.body?.is_error === 0) {
          this.selectedDoc.uploaded = true;
          this.selectedDoc.result_doc = URL.createObjectURL(file);
          if (this.claimId) {
            this.uploadedFiles.push({ file, document_type: this.selectedDoc.document_type, more_id: this.selectedDoc.more_id });
          } else {
            this.uploadedFiles.push({ file, document_type: this.selectedDoc.document_type });
          }
          this.closeUploadFromModal();
        } else if (res?.body?.is_error === 1) {
          this.shared.customPopups(res.body.original_text, 1);
          this.closeUploadFromModal();
        }
        this.loader.hideLoader();
      },
      err => {
        this.shared.customPopups('Error uploading document: ' + err.message, 1);
        this.loader.hideLoader();
        this.closeUploadFromModal();
      }
    );
  }

  allDocumentsUploaded(): boolean {
    return this.claimDocs.every(doc => !doc.required || doc.uploaded);
  }

  canSubmit(): boolean {
    return this.allDocumentsUploaded();
  }

  submitDocument(): void {
    if (!this.canSubmit()) {
      this.shared.customPopups('Please complete all required documents to submit your claim.', 1);
      return;
    }

    const formData = new FormData();

    formData.append('doctor_id', this.doctorId);

    let api = 'upload_multiple_claim_doc';

    if (this.claimId) {
      const docTypes: { document_type: number, more_id?: number }[] = [];
      api = 'upload_multiple_more_doc'
      this.uploadedFiles.forEach((entry, index) => {
        formData.append('document_file', entry.file);
        docTypes[index] = { document_type: entry.document_type, more_id: entry.more_id };
      });
      formData.append('contract_id', this.contractId)
      formData.append('claim_id', this.claimId)
      formData.append('document_type', JSON.stringify(docTypes));

    } else {
      const docTypes: number[] = [];
      formData.append('temp_claim_id', this.tempClaimId || '0');
      this.uploadedFiles.forEach((entry, index) => {
        formData.append('document_file', entry.file);
        docTypes[index] = entry.document_type;
      });
      formData.append('document_type', JSON.stringify(docTypes));
    }

    this.loader.showLoader();
    this.api.uploadApi(formData, `auth/${api}`).subscribe(
      (data) => {
        if (data?.body?.is_error == 0) {
          this.shared.customPopups('Claim Submitted Successfully!', 3);
          this.currentStep = 2;
        }
        if (data?.body?.is_error == 1) {
          if (data?.body?.original_text) {
            this.shared.customPopups(data?.body?.original_text, 1);
          } else {
            this.shared.customPopups(data?.body?.original_text, 1);
          }
        }
        this.loader.hideLoader();
      },
      () => {
        this.shared.customPopups('Error submitting claim', 1);
        this.loader.hideLoader();
      }
    );
  }

  triggerCameraInput(): void {
    this.cameraInput.nativeElement.click();
  }

  triggerGalleryInput(): void {
    this.galleryInput.nativeElement.click();
  }

  onCameraUpload(): void {
    this.closeUploadFromModal();
    // Implement camera upload logic here
  }

  onGalleryUpload(): void {
    this.closeUploadFromModal();
    // Implement gallery upload logic here
  }
}
